import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, Loading, Tag } from "@/components/common";
import {
  Horse, Sparkle, FloppyDisk, Image as ImageIcon, VideoCamera, Spinner,
  CheckCircle, WarningCircle, MagicWand, DeviceMobile, ShieldCheck,
  ArrowClockwise, DownloadSimple, ArrowSquareOut, FilmStrip
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATE_STYLE = {
  COMPLETED: "text-[#00FF9D] border-[#00FF9D]/35 bg-[#00FF9D]/10",
  FAILED: "text-[#FF6B7A] border-[#FF6B7A]/35 bg-[#FF6B7A]/10",
  NOT_CONFIGURED: "text-[#FFB800] border-[#FFB800]/35 bg-[#FFB800]/10",
  QUEUED: "text-[#6FA9A5] border-[#6FA9A5]/35 bg-[#276A6B]/15",
  RUNNING: "text-[#6FA9A5] border-[#6FA9A5]/35 bg-[#276A6B]/15",
  PROCESSING: "text-[#6FA9A5] border-[#6FA9A5]/35 bg-[#276A6B]/15",
};

export default function PinPilotCampaignStudio() {
  const [app, setApp] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [queueing, setQueueing] = useState("");

  const loadJobs = async () => {
    const { data } = await api.get("/media/jobs");
    setJobs((data.jobs || []).filter((j) => ["app_campaign_scene", "app_campaign_film"].includes(j.ref_type)));
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: apps } = await api.get("/apps");
        const pinpilot = apps.find((a) => (a.name || "").toLowerCase().includes("pinpilot"));
        if (!pinpilot) { setLoaded(true); return; }
        setApp(pinpilot);
        const [{ data: c }] = await Promise.all([
          api.get(`/apps/${pinpilot.id}/campaign`),
          loadJobs(),
        ]);
        setCampaign(c);
        setLoaded(true);
      } catch (e) {
        setLoaded(true);
        toast.error(e.response?.data?.detail || "Campaign Studio could not load");
      }
    })();
  }, []);

  useEffect(() => {
    if (!jobs.some((j) => ["QUEUED", "RUNNING", "PROCESSING"].includes(j.status))) return;
    const timer = setInterval(() => loadJobs().catch(() => {}), 4000);
    return () => clearInterval(timer);
  }, [jobs]);

  const campaignJobs = useMemo(() => campaign
    ? jobs.filter((j) => j.campaign_id === campaign.id || j.ref_id === campaign.id || (j.ref_id || "").startsWith(`${campaign.id}:`))
    : [], [jobs, campaign]);

  const jobsByScene = useMemo(() => {
    const map = {};
    campaignJobs.forEach((j) => {
      const sceneId = (j.ref_id || "").split(":").pop();
      map[sceneId] ||= {};
      if (!map[sceneId][j.capability]) map[sceneId][j.capability] = j;
      if (j.status === "COMPLETED" && j.asset_url && !map[sceneId][`completed_${j.capability}`]) {
        map[sceneId][`completed_${j.capability}`] = j;
      }
    });
    return map;
  }, [campaignJobs]);

  const outputs = useMemo(() => campaignJobs
    .filter((j) => j.status === "COMPLETED" && j.asset_url)
    .slice(0, 12), [campaignJobs]);
  const activeJobs = useMemo(() => campaignJobs
    .filter((j) => ["QUEUED", "RUNNING", "PROCESSING"].includes(j.status)), [campaignJobs]);

  const patchScene = (id, fields) => setCampaign((c) => ({
    ...c, scenes: c.scenes.map((s) => s.id === id ? { ...s, ...fields } : s)
  }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/apps/${app.id}/campaign`, {
        data: {
          name: campaign.name, concept: campaign.concept, format: campaign.format,
          duration_seconds: campaign.duration_seconds, character: campaign.character,
          world_rules: campaign.world_rules, scenes: campaign.scenes,
        }
      });
      setCampaign(data);
      toast.success("Campaign canon saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const queue = async (scene, capability) => {
    const key = `${scene.id}:${capability}`;
    setQueueing(key);
    try {
      const { data } = await api.post(
        `/apps/${app.id}/campaign/scenes/${scene.id}/queue`,
        { data: { capability, prompt: scene.prompt } }
      );
      setJobs((j) => [data, ...j]);
      toast.success(`${scene.title} added to the real media queue`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not queue scene");
    } finally { setQueueing(""); }
  };

  const queueAll = async (capability) => {
    const label = capability === "image" ? "stills" : "cinematic clips";
    setQueueing(`all:${capability}`);
    try {
      let added = 0;
      for (const scene of campaign.scenes) {
        const ready = jobsByScene[scene.id]?.[`completed_${capability}`]?.asset_url;
        if (ready) continue;
        if (capability === "video" && !jobsByScene[scene.id]?.completed_image?.asset_url) {
          throw new Error(`Scene ${scene.order} needs a still first`);
        }
        const { data } = await api.post(
          `/apps/${app.id}/campaign/scenes/${scene.id}/queue`,
          { data: { capability, prompt: scene.prompt } }
        );
        setJobs((current) => [data, ...current.filter((j) => j.id !== data.id)]);
        added += 1;
      }
      toast.success(added ? `${added} ${label} queued` : `All ${label} are already ready`);
      await loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || `Could not queue ${label}`);
    } finally { setQueueing(""); }
  };

  const queueFilm = async () => {
    setQueueing("film");
    try {
      const { data } = await api.post(`/apps/${app.id}/campaign/film/queue`);
      setJobs((current) => [data, ...current.filter((j) => j.id !== data.id)]);
      toast.success("Final 30-second film assembly queued");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assemble the film");
    } finally { setQueueing(""); }
  };

  const retry = async (job) => {
    setQueueing(`retry:${job.id}`);
    try {
      await api.post(`/media/jobs/${job.id}/retry`);
      await loadJobs();
      toast.success("Generation queued again");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally { setQueueing(""); }
  };

  if (!loaded) return (
    <div>
      <PageHeader icon={MagicWand} title="PinPilot Campaign Studio" subtitle="Action-first old-world delivery comedy production." />
      <Loading />
      <p className="mt-4 text-sm text-zinc-500">Looking for the PinPilot project…</p>
    </div>
  );
  if (!app) return (
    <div>
      <PageHeader icon={MagicWand} title="PinPilot Campaign Studio" subtitle="Old-world courier campaign production." />
      <Panel><div className="flex gap-3 items-start"><WarningCircle size={22} className="text-[#FFB800]" />
        <div><div className="font-semibold text-white">PinPilot app project is required</div>
        <p className="text-sm text-zinc-400 mt-1">Create “PinPilot” in App Studio first, then return here.</p></div></div></Panel>
    </div>
  );
  if (!campaign) return <Loading />;

  return (
    <div>
      <PageHeader icon={MagicWand} title="PinPilot Campaign Studio"
        subtitle="Fast visual comedy with the wizard, horse and cart always in motion — built for vertical social video." />

      <div className="grid lg:grid-cols-[1.45fr_.55fr] gap-5 mb-6">
        <Panel className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#276A6B]/20 via-transparent to-[#B88746]/10 pointer-events-none" />
          <div className="relative">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Tag>PINPILOT</Tag><Tag>VERTICAL 9:16</Tag><Tag>30 SEC</Tag>
              <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#00FF9D]"><CheckCircle weight="fill" /> CANON SAVED</span>
            </div>
            <input value={campaign.name} onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
              className="w-full bg-transparent text-3xl md:text-4xl font-display font-bold text-white outline-none border-b border-white/10 pb-3 focus:border-[#6FA9A5]" />
            <textarea rows={3} value={campaign.concept} onChange={(e) => setCampaign({ ...campaign, concept: e.target.value })}
              className="mt-4 w-full rounded-xl bg-black/25 border border-white/10 p-3 text-sm text-zinc-300 outline-none focus:border-[#6FA9A5]" />
            <button onClick={save} disabled={saving}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#276A6B] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#174A4B] disabled:opacity-60">
              {saving ? <Spinner className="animate-spin" /> : <FloppyDisk weight="fill" />} Save campaign
            </button>
          </div>
        </Panel>

        <Panel>
          <div className="flex items-center gap-2 mb-3"><Horse size={24} className="text-[#B88746]" />
            <h3 className="font-display font-bold text-white">Character lock</h3></div>
          {campaign.character?.reference_asset_url && (
            <div className="relative mb-4 overflow-hidden rounded-2xl border border-[#B88746]/35 bg-black/30">
              <img src={campaign.character.reference_asset_url} alt="The Wayfinder official master reference"
                className="h-64 w-full object-cover object-top" />
              <div className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-full border border-[#00FF9D]/40 bg-black/80 px-3 py-1 text-[10px] font-semibold text-[#00FF9D]">
                <ShieldCheck weight="fill" /> OFFICIAL MASTER LOCKED
              </div>
            </div>
          )}
          <div className="text-lg font-semibold text-[#F5F1E8]">{campaign.character?.name}</div>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{campaign.character?.continuity}</p>
          <div className="mt-4 rounded-xl border border-[#FFB800]/25 bg-[#FFB800]/8 p-3 text-xs text-[#E7C985]">
            {campaign.character?.rule}
          </div>
          <div className="flex items-center gap-2 mt-4 text-xs text-zinc-500"><DeviceMobile /> Only the phone and PinPilot UI are modern.</div>
        </Panel>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div><h2 className="font-display text-xl font-bold text-white">Production board</h2>
          <p className="text-xs text-zinc-500 mt-1">Every shot uses the locked Wayfinder master and must contain movement, reaction or payoff. No static talking-head scenes.</p></div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => queueAll("image")} disabled={!!queueing}
            className="inline-flex items-center gap-2 rounded-full border border-[#276A6B]/60 bg-[#276A6B]/15 px-3 py-1.5 text-xs font-semibold text-[#8CC5C0] disabled:opacity-50">
            {queueing === "all:image" ? <Spinner className="animate-spin" /> : <ImageIcon />} Generate all stills
          </button>
          <button onClick={() => queueAll("video")} disabled={!!queueing}
            className="inline-flex items-center gap-2 rounded-full border border-[#B88746]/50 bg-[#B88746]/10 px-3 py-1.5 text-xs font-semibold text-[#E7C985] disabled:opacity-50">
            {queueing === "all:video" ? <Spinner className="animate-spin" /> : <VideoCamera />} Generate all clips
          </button>
          <button onClick={queueFilm} disabled={!!queueing}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#276A6B] to-[#B88746] px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50">
            {queueing === "film" ? <Spinner className="animate-spin" /> : <FilmStrip weight="fill" />} Assemble final film
          </button>
          <button onClick={() => loadJobs().catch(() => toast.error("Could not refresh jobs"))}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-300">
            <ArrowClockwise /> Refresh
          </button>
          <Tag>{campaign.scenes.length} SHOTS</Tag>
        </div>
      </div>

      {(activeJobs.length > 0 || outputs.length > 0) && (
        <Panel className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <FilmStrip className="text-[#6FA9A5]" weight="fill" />
            <h3 className="font-display font-bold text-white">Campaign Outputs</h3>
            <span className="ml-auto text-[11px] text-zinc-500">{outputs.length} ready · {activeJobs.length} active</span>
          </div>
          {activeJobs.length > 0 && (
            <div className="space-y-2 mb-4">
              {activeJobs.map((job) => (
                <div key={job.id} className="rounded-xl border border-[#276A6B]/40 bg-[#276A6B]/10 p-3">
                  <div className="flex justify-between gap-3 text-xs">
                    <span className="font-semibold text-[#B7D7D3]">{job.capability === "film" ? "Final film" : job.capability === "video" ? "Video clip" : "Scene still"} · {job.stage || job.status}</span>
                    <span className="text-zinc-400">{job.progress || 0}%</span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/5">
                    <div className="h-full bg-[#6FA9A5] transition-all" style={{ width: `${Math.max(2, job.progress || 0)}%` }} />
                  </div>
                  <div className="mt-2 text-[10px] text-zinc-600">Job {job.id}</div>
                </div>
              ))}
            </div>
          )}
          {outputs.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {outputs.map((job) => {
                const sceneId = (job.ref_id || "").split(":").pop();
                const scene = campaign.scenes.find((s) => s.id === sceneId);
                const isFilm = job.capability === "film";
                return (
                  <div key={job.id} className="overflow-hidden rounded-xl border border-white/10 bg-black/25">
                    {(job.capability === "video" || isFilm)
                      ? <video src={job.asset_url} controls playsInline preload="metadata" className="aspect-[9/16] max-h-80 w-full bg-black object-cover" />
                      : <img src={job.asset_url} alt={scene?.title || "Campaign output"} className="aspect-[9/16] max-h-80 w-full object-cover" />}
                    <div className="p-3">
                      <div className="truncate text-xs font-semibold text-white">{isFilm ? "Final 30-second campaign film" : (scene?.title || "Campaign scene")}</div>
                      <div className="mt-2 flex gap-2">
                        <a href={job.asset_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-[#8CC5C0]"><ArrowSquareOut /> Open</a>
                        <a href={job.asset_url} download
                          className="inline-flex items-center gap-1 text-[11px] text-[#E7C985]"><DownloadSimple /> Download</a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      )}

      <div className="space-y-4">
        {campaign.scenes.map((scene) => {
          const sceneJobs = jobsByScene[scene.id] || {};
          const stillJob = sceneJobs.image;
          const clipJob = sceneJobs.video;
          const completedStillJob = sceneJobs.completed_image;
          const completedClipJob = sceneJobs.completed_video;
          const job = [stillJob, clipJob].find((j) => ["QUEUED", "RUNNING", "PROCESSING"].includes(j?.status)) || clipJob || stillJob;
          const status = job?.status || "READY";
          const stillReady = Boolean(completedStillJob?.asset_url);
          return (
            <Panel key={scene.id}>
              <div className="grid lg:grid-cols-[90px_1fr_220px] gap-4">
                <div>
                  <div className="text-4xl font-display font-bold text-white/20">{String(scene.order).padStart(2, "0")}</div>
                  <div className="text-xs text-zinc-500 mt-1">{scene.seconds}s</div>
                </div>
                <div>
                  <input value={scene.title} onChange={(e) => patchScene(scene.id, { title: e.target.value })}
                    className="w-full bg-transparent font-display text-lg font-bold text-white outline-none" />
                  <textarea rows={2} value={scene.shot} onChange={(e) => patchScene(scene.id, { shot: e.target.value })}
                    className="mt-2 w-full rounded-xl bg-black/25 border border-white/8 p-3 text-sm text-zinc-300 outline-none focus:border-[#6FA9A5]" />
                  {scene.sound_beat && <div className="mt-2 text-xs text-[#B7D7D3]">SOUND — {scene.sound_beat}</div>}
                  <textarea rows={3} value={scene.prompt} onChange={(e) => patchScene(scene.id, { prompt: e.target.value })}
                    className="mt-3 w-full rounded-xl bg-black/25 border border-white/8 p-3 text-xs text-zinc-400 outline-none focus:border-[#6FA9A5]" />
                </div>
                <div className="flex lg:flex-col gap-2 lg:items-stretch">
                  <span className={`rounded-full border px-3 py-1.5 text-center text-[11px] font-semibold ${STATE_STYLE[status] || "text-zinc-400 border-white/10"}`}>
                    {job?.stage ? job.stage.replaceAll("_", " ") : status.replaceAll("_", " ")}
                    {job && ["QUEUED", "RUNNING", "PROCESSING"].includes(job.status) ? ` · ${job.progress || 0}%` : ""}
                  </span>
                  <button onClick={() => queue(scene, "image")} disabled={!!queueing || !campaign.character?.reference_locked || ["QUEUED", "RUNNING", "PROCESSING"].includes(stillJob?.status)}
                    className="inline-flex justify-center items-center gap-2 rounded-xl border border-[#276A6B]/60 bg-[#276A6B]/15 px-3 py-2 text-xs font-semibold text-[#8CC5C0] hover:bg-[#276A6B]/25 disabled:opacity-50">
                    {queueing === `${scene.id}:image` ? <Spinner className="animate-spin" /> : <ImageIcon />} Generate still
                  </button>
                  <button onClick={() => queue(scene, "video")} disabled={!!queueing || !stillReady || ["QUEUED", "RUNNING", "PROCESSING"].includes(clipJob?.status)}
                    className="inline-flex justify-center items-center gap-2 rounded-xl border border-[#B88746]/50 bg-[#B88746]/10 px-3 py-2 text-xs font-semibold text-[#E7C985] hover:bg-[#B88746]/20 disabled:opacity-50">
                    {queueing === `${scene.id}:video` ? <Spinner className="animate-spin" /> : <VideoCamera />} Generate cinematic clip
                  </button>
                  {!stillReady && <p className="text-[10px] leading-relaxed text-zinc-500">Create the still first; then animate it.</p>}
                  {stillReady && (
                    <a href={completedStillJob.asset_url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-xl border border-white/10 bg-black/30">
                      <img src={completedStillJob.asset_url} alt={`${scene.title} generated still`} className="h-40 w-full object-cover" />
                    </a>
                  )}
                  {completedClipJob?.asset_url && (
                    <video src={completedClipJob.asset_url} controls playsInline className="w-full rounded-xl border border-[#B88746]/30 bg-black" />
                  )}
                  {stillJob?.error && <p className="rounded-lg border border-[#FF6B7A]/20 bg-[#FF6B7A]/5 p-2 text-[10px] leading-relaxed text-[#FF9AA5]">{stillJob.error}</p>}
                  {clipJob?.error && <p className="rounded-lg border border-[#FF6B7A]/20 bg-[#FF6B7A]/5 p-2 text-[10px] leading-relaxed text-[#FF9AA5]">{clipJob.error}</p>}
                  {job?.status === "FAILED" && (
                    <button onClick={() => retry(job)} disabled={!!queueing}
                      className="inline-flex justify-center items-center gap-2 rounded-xl border border-[#FF6B7A]/35 px-3 py-2 text-xs font-semibold text-[#FF9AA5] disabled:opacity-50">
                      {queueing === `retry:${job.id}` ? <Spinner className="animate-spin" /> : <ArrowClockwise />} Retry failed job
                    </button>
                  )}
                </div>
              </div>
            </Panel>
          );
        })}
      </div>

      <Panel className="mt-5">
        <div className="flex items-center gap-2 mb-3"><Sparkle className="text-[#6FA9A5]" weight="fill" />
          <h3 className="font-display font-bold text-white">Continuity rules</h3></div>
        <div className="grid sm:grid-cols-2 gap-2">
          {campaign.world_rules.map((rule, i) => (
            <div key={i} className="rounded-xl border border-white/8 bg-black/20 p-3 text-xs text-zinc-300">{rule}</div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
